<template>
  <div v-if="isVisible" class="page-preloader" :class="{ 'fade-out': isFadingOut }">
    <div class="preloader-content">
      <!-- Logo/Brand -->
      <div class="preloader-logo">
        <img 
          v-if="logo" 
          :src="logo" 
          :alt="brandName" 
          class="logo-image"
        />
        <div v-else class="logo-placeholder">
          <v-icon size="48" color="primary">{{ brandIcon }}</v-icon>
        </div>
      </div>

      <!-- Brand Name -->
      <h1 class="brand-name">{{ brandName }}</h1>

      <!-- Loading Animation -->
      <div class="loading-animation">
        <div class="spinner-container">
          <div class="spinner" :class="spinnerType">
            <div v-if="spinnerType === 'dots'" class="dots">
              <div class="dot"></div>
              <div class="dot"></div>
              <div class="dot"></div>
            </div>
            <div v-else-if="spinnerType === 'pulse'" class="pulse"></div>
            <div v-else-if="spinnerType === 'wave'" class="wave">
              <div class="wave-bar"></div>
              <div class="wave-bar"></div>
              <div class="wave-bar"></div>
              <div class="wave-bar"></div>
              <div class="wave-bar"></div>
            </div>
            <div v-else class="circular">
              <div class="circular-inner"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading Text -->
      <div class="loading-text">
        <p class="main-text">{{ currentText }}</p>
        <p v-if="subText" class="sub-text">{{ subText }}</p>
      </div>

      <!-- Progress Bar (if progress is provided) -->
      <div v-if="showProgress && progress !== null" class="progress-container">
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :style="{ width: progress + '%' }"
          ></div>
        </div>
        <span class="progress-text">{{ progress }}%</span>
      </div>

      <!-- Loading Steps (if steps are provided) -->
      <div v-if="steps && steps.length > 0" class="loading-steps">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="step-item"
          :class="{ 
            'completed': index < currentStep, 
            'active': index === currentStep,
            'pending': index > currentStep
          }"
        >
          <div class="step-icon">
            <v-icon v-if="index < currentStep" size="16" color="success">mdi-check</v-icon>
            <v-icon v-else-if="index === currentStep" size="16" color="primary">mdi-loading</v-icon>
            <span v-else class="step-number">{{ index + 1 }}</span>
          </div>
          <span class="step-text">{{ step }}</span>
        </div>
      </div>
    </div>

    <!-- Background Animation -->
    <div class="preloader-background">
      <div class="bg-animation" :class="backgroundAnimation"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PagePreloader',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    brandName: {
      type: String,
      default: 'Wasata'
    },
    brandIcon: {
      type: String,
      default: 'mdi-home'
    },
    logo: {
      type: String,
      default: null
    },
    loadingText: {
      type: String,
      default: 'Loading...'
    },
    subText: {
      type: String,
      default: ''
    },
    spinnerType: {
      type: String,
      default: 'circular', // 'circular', 'dots', 'pulse', 'wave'
      validator: value => ['circular', 'dots', 'pulse', 'wave'].includes(value)
    },
    backgroundAnimation: {
      type: String,
      default: 'gradient', // 'gradient', 'particles', 'waves', 'none'
      validator: value => ['gradient', 'particles', 'waves', 'none'].includes(value)
    },
    progress: {
      type: Number,
      default: null
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    steps: {
      type: Array,
      default: () => []
    },
    currentStep: {
      type: Number,
      default: 0
    },
    autoHide: {
      type: Boolean,
      default: false
    },
    autoHideDelay: {
      type: Number,
      default: 3000
    }
  },
  data() {
    return {
      isFadingOut: false,
      currentText: this.loadingText,
      textIndex: 0,
      loadingTexts: [
        'Loading...',
        'Initializing...',
        'Preparing data...',
        'Almost ready...'
      ]
    };
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.startLoading();
      } else {
        this.fadeOut();
      }
    },
    loadingText(newVal) {
      this.currentText = newVal;
    }
  },
  mounted() {
    if (this.isVisible) {
      this.startLoading();
    }
  },
  methods: {
    startLoading() {
      this.isFadingOut = false;
      this.currentText = this.loadingText;
      
      // Start text rotation if multiple texts are provided
      if (this.loadingTexts.length > 1) {
        this.startTextRotation();
      }
      
      // Auto hide if enabled
      if (this.autoHide) {
        setTimeout(() => {
          this.hide();
        }, this.autoHideDelay);
      }
    },
    
    startTextRotation() {
      const interval = setInterval(() => {
        if (!this.isVisible) {
          clearInterval(interval);
          return;
        }
        
        this.textIndex = (this.textIndex + 1) % this.loadingTexts.length;
        this.currentText = this.loadingTexts[this.textIndex];
      }, 1500);
    },
    
    fadeOut() {
      this.isFadingOut = true;
      setTimeout(() => {
        this.$emit('hidden');
      }, 500);
    },
    
    hide() {
      this.$emit('hide');
    },
    
    updateProgress(progress) {
      this.$emit('update:progress', progress);
    },
    
    nextStep() {
      this.$emit('update:currentStep', this.currentStep + 1);
    }
  }
};
</script>

<style scoped>
.page-preloader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  opacity: 1;
  transition: opacity 0.5s ease;
}

.page-preloader.fade-out {
  opacity: 0;
}

.preloader-content {
  text-align: center;
  color: white;
  z-index: 2;
  position: relative;
}

.preloader-logo {
  margin-bottom: 20px;
}

.logo-image {
  max-width: 80px;
  max-height: 80px;
  border-radius: 50%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.logo-placeholder {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.brand-name {
  font-size: 2.5rem;
  font-weight: 300;
  margin: 0 0 30px 0;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.loading-animation {
  margin: 30px 0;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Circular Spinner */
.circular {
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.circular-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
}

/* Dots Spinner */
.dots {
  display: flex;
  gap: 8px;
}

.dot {
  width: 12px;
  height: 12px;
  background: white;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}

.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }
.dot:nth-child(3) { animation-delay: 0s; }

/* Pulse Spinner */
.pulse {
  width: 50px;
  height: 50px;
  background: white;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

/* Wave Spinner */
.wave {
  display: flex;
  gap: 4px;
  align-items: end;
}

.wave-bar {
  width: 6px;
  background: white;
  border-radius: 3px;
  animation: wave 1.2s ease-in-out infinite;
}

.wave-bar:nth-child(1) { height: 20px; animation-delay: 0s; }
.wave-bar:nth-child(2) { height: 30px; animation-delay: 0.1s; }
.wave-bar:nth-child(3) { height: 40px; animation-delay: 0.2s; }
.wave-bar:nth-child(4) { height: 30px; animation-delay: 0.3s; }
.wave-bar:nth-child(5) { height: 20px; animation-delay: 0.4s; }

.loading-text {
  margin: 20px 0;
}

.main-text {
  font-size: 1.2rem;
  font-weight: 500;
  margin: 0 0 8px 0;
  opacity: 0.9;
}

.sub-text {
  font-size: 0.9rem;
  opacity: 0.7;
  margin: 0;
}

.progress-container {
  margin: 30px 0;
  width: 300px;
  margin-left: auto;
  margin-right: auto;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: white;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.9rem;
  opacity: 0.8;
}

.loading-steps {
  margin: 30px 0;
  text-align: left;
  max-width: 300px;
  margin-left: auto;
  margin-right: auto;
}

.step-item {
  display: flex;
  align-items: center;
  margin: 12px 0;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.step-item.active {
  opacity: 1;
}

.step-item.completed {
  opacity: 0.8;
}

.step-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.step-item.completed .step-icon {
  background: rgba(76, 175, 80, 0.8);
}

.step-item.active .step-icon {
  background: rgba(255, 255, 255, 0.3);
}

.step-number {
  font-size: 0.8rem;
  font-weight: 600;
}

.step-text {
  font-size: 0.9rem;
}

.preloader-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.bg-animation {
  width: 100%;
  height: 100%;
}

.bg-animation.gradient {
  background: linear-gradient(45deg, 
    rgba(102, 126, 234, 0.8) 0%, 
    rgba(118, 75, 162, 0.8) 25%,
    rgba(255, 107, 107, 0.8) 50%,
    rgba(118, 75, 162, 0.8) 75%,
    rgba(102, 126, 234, 0.8) 100%);
  background-size: 400% 400%;
  animation: gradientShift 8s ease infinite;
}

.bg-animation.particles {
  background: radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  animation: particleFloat 12s ease-in-out infinite;
}

.bg-animation.waves {
  background: linear-gradient(45deg, 
    rgba(255, 255, 255, 0.1) 0%, 
    transparent 50%,
    rgba(255, 255, 255, 0.1) 100%);
  animation: waveMove 6s ease-in-out infinite;
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
}

@keyframes wave {
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
}

@keyframes gradientShift {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

@keyframes particleFloat {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(120deg); }
  66% { transform: translateY(10px) rotate(240deg); }
}

@keyframes waveMove {
  0%, 100% { transform: translateX(-100%); }
  50% { transform: translateX(100%); }
}

/* Dark mode support */
.dark .page-preloader {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
}

.dark .preloader-content {
  color: #f3f5f7;
}

.dark .logo-placeholder {
  background: rgba(243, 245, 247, 0.1);
}

.dark .step-icon {
  background: rgba(243, 245, 247, 0.1);
}

.dark .step-item.completed .step-icon {
  background: rgba(76, 175, 80, 0.6);
}

.dark .step-item.active .step-icon {
  background: rgba(243, 245, 247, 0.2);
}

/* Responsive design */
@media (max-width: 768px) {
  .brand-name {
    font-size: 2rem;
  }
  
  .preloader-content {
    padding: 0 20px;
  }
  
  .progress-container {
    width: 250px;
  }
  
  .loading-steps {
    max-width: 250px;
  }
}

@media (max-width: 480px) {
  .brand-name {
    font-size: 1.5rem;
  }
  
  .logo-image, .logo-placeholder {
    max-width: 60px;
    max-height: 60px;
  }
  
  .progress-container {
    width: 200px;
  }
  
  .loading-steps {
    max-width: 200px;
  }
}
</style>
